import { useContext } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { ActuatorAccessoriesPageData } from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { calculateTotals } from 'src/actuators/checkout/CalculateTotals.ts';
import { Price } from 'src/common/Price/Price';
import { SelectionDataContext } from 'src/projects/components/SelectionDataContext.ts';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { Counter } from 'src/common/Counter/Counter.tsx';
import { WebAppSelection } from 'shared/projects/types.ts';
import { removeByLabel, updateQtyByLabel } from 'src/projects/utils/updateQtyByLabel.ts';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';

type FetchActuatorSelectionProps = {
    selection: WebAppSelection;
    updateSelection: (selection: WebAppSelection) => void;
}

const titleMap = {
    retrofit_valves: MESSAGES.checkoutValveAndActuatorTitle,
    retrofit_actuators: MESSAGES.checkoutActuatorTitle,
    retrofit_sensors: MESSAGES.checkoutSensorsTitle,
};

export const FetchWebAppSelection = ({
    selection,
    updateSelection,
} : FetchActuatorSelectionProps) => {
    const { userSettings, currency } = useContext(ConfigContext);
    const ctx = useContext(SelectionDataContext);

    const data = (ctx?.data ?? undefined) as ActuatorAccessoriesPageData | undefined;
    if (ctx === null || ctx.loading || data === undefined) {
        return <SkeletonSelection/>;
    }

    const priceCurrency = ctx.currency;
    const { pricePerUnit } = calculateTotals(data.accessories, userSettings, {
        actuatorLabel: selection.productLabel,
        qty: 1,
        selectedAccessories: selection.selectedAccessories,
        actuatorPage: '1',
        checkoutComment: '',
        sent: false,
    }, currency);
    const accessories = prepareAccessories<
    ActuatorAccessoriesPageData['accessories']['accessories']['0']
    >(
        data.accessories.accessories,
        selection.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessories(a.accessory, b.accessory),
    );
    return (
        <>
            <ProjectSelectionDeviceList
                title={<I18n messageId={titleMap[selection.type] ?? ''}/>}
            >
                <ProjectSelectionDevice
                    productImage={(
                        <ProductImage
                            imgSrc={data.accessories.actuator.image?.raw.join('') || ''}
                        >
                            <ActuatorIcon/>
                        </ProductImage>
                    )}
                    productLink={(
                        <ViewProductLink
                            userSettings={userSettings}
                            code={data.accessories.actuator.Label}
                            content=""
                        >
                            <span className="vss-device__product-label">
                                {data.accessories.actuator.Label}
                            </span>

                            <div className="vss-device__product-icon">
                                <ArrowRightIcon/>
                            </div>
                        </ViewProductLink>
                    )}
                    productDescription={(
                        <RenderHtml
                            data={data.accessories.actuator.productType?.localized.join('') || ''}
                        />
                    )}
                    quantity={(
                        <Counter
                            value={selection.qty}
                            onChange={(qty) => {
                                updateSelection(
                                    {
                                        ...selection,
                                        qty,
                                        exportData: updateQtyByLabel(
                                            selection.exportData,
                                            selection.productLabel,
                                            qty,
                                        ),
                                    },
                                );
                            }}
                        />
                    )}
                    price={(
                        <Price
                            price={pricePerUnit}
                            currency={priceCurrency}
                            locale={userSettings.currentLocale}
                        />
                    )}
                />
            </ProjectSelectionDeviceList>

            {accessories.length ? (
                <ProjectSelectionDeviceList
                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                >
                    {accessories.map(({ qty, accessory: { item }, price }) => (
                        <ProjectSelectionDevice
                            key={item.Label}
                            productImage={(
                                <ProductImage
                                    imgSrc={item.image?.raw.join('') || ''}
                                >
                                    <ActuatorIcon/>
                                </ProductImage>
                            )}
                            productLink={(
                                <ViewProductLink
                                    userSettings={userSettings}
                                    code={item.Label}
                                    content=""
                                >
                                    {item.Label}

                                    <div className="vss-accessory__product-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                </ViewProductLink>
                            )}
                            productDescription={<RenderHtml data={item.accessoryType?.localized.join('') || ''}/>}
                            quantity={(
                                <Counter
                                    value={qty}
                                    onDelete={() => {
                                        updateSelection(
                                            {
                                                ...selection,
                                                selectedAccessories: selection.selectedAccessories.filter(
                                                    (acc) => acc.label !== item.Label,
                                                ),
                                                exportData: removeByLabel(
                                                    selection.exportData,
                                                    item.Label,
                                                ),
                                            },
                                        );
                                    }}
                                    onChange={(quantity) => {
                                        updateSelection(
                                            {
                                                ...selection,
                                                selectedAccessories: selection.selectedAccessories.map(
                                                    (acc) => {
                                                        if (acc.label === item.Label) {
                                                            return {
                                                                ...acc,
                                                                qty: quantity,
                                                            };
                                                        }
                                                        return acc;
                                                    },
                                                ),
                                                exportData: updateQtyByLabel(
                                                    selection.exportData,
                                                    item.Label,
                                                    quantity,
                                                ),
                                            },
                                        );
                                    }}
                                />
                            )}
                            price={(
                                <Price
                                    price={price}
                                    currency={priceCurrency}
                                    locale={userSettings.currentLocale}
                                />
                            )}
                        />
                    ))}
                </ProjectSelectionDeviceList>
            ) : null}
        </>
    );
};
